import type { AdminMoneyHealthHoldScanDto, AdminMoneyHealthListDto } from '@carcheck/shared';

import { formatDateTime, formatDurationShort } from '../../../utils/format';

/**
 * AUT-328 — every sentence on the money-health tab that depends on a number
 * the server sent, expressed as an i18n key plus its interpolations.
 *
 * Same shape and same reason as `CaptureStoryText` (rule #11): no Russian in
 * a `.ts` file, and the branch that decides WHICH sentence gets said is a unit
 * test rather than something you only see when a list happens to be capped.
 *
 * Three honesty rules live here, because getting any of them wrong turns the
 * screen into the thing it was built to replace:
 *
 *  1. **`total` is a floor when `countExact` is false.** It renders with a `≥`
 *     and never without one — the two hold lists are decided in application
 *     code from a bounded scan, so past the cap the server genuinely does not
 *     know. A bare "118" would be a claim nobody can make.
 *  2. **Truncation is stated, never paged.** «Показаны N из ≥M» — the cap comes
 *     off `list.limit` / `items.length`, never a hardcoded 25.
 *  3. **A missing `holdExpiresAt` is "не задан", never "истёк".** `null` means
 *     no usable deadline; reading it as expired invents an emergency.
 */
export interface MoneyHealthText {
  key: string;
  values?: Readonly<Record<string, string | number>>;
}

const KEY = 'moneyHealth';

/**
 * The count badge for one list: `118` when exact, `≥ 118` when the scan was
 * capped. `AdminStuckWebhooksDto.rowCount` never goes through here — it is
 * summed from a full aggregate and is always exact.
 */
export const countText = <T>(list: AdminMoneyHealthListDto<T>): MoneyHealthText => ({
  key: list.countExact ? `${KEY}.count.exact` : `${KEY}.count.atLeast`,
  values: { total: list.total },
});

/**
 * «Показаны 25 из ≥ 118 — список обрезан сервером.», or `null` when the list
 * is whole. `totalLabel` is the already-rendered `countText`, so the `≥`
 * survives into this sentence too.
 */
export const truncationText = <T>(
  list: AdminMoneyHealthListDto<T>,
  totalLabel: string,
): MoneyHealthText | null =>
  list.truncated
    ? { key: `${KEY}.truncated`, values: { shown: list.items.length, total: totalLabel } }
    : null;

/**
 * What the one live-hold pass saw. Rendered ALWAYS, not only when capped: the
 * "500 из 500, счётчики — это минимум" line and the "128 из 500, счётчики
 * точные" line answer the same question, and only one of them is reassuring.
 */
export const scanText = (scan: AdminMoneyHealthHoldScanDto): MoneyHealthText => ({
  key: scan.capped ? `${KEY}.scan.capped` : `${KEY}.scan.clear`,
  values: { scanned: scan.scanned, cap: scan.scanCap },
});

/**
 * A server-owned threshold as a plural-correct Russian quantity: minutes under
 * an hour, hours under two days, days beyond. The unit keys are all GENITIVE
 * («дольше 15 минут», «меньше 24 часов») and every copy that interpolates one
 * is phrased to govern the genitive — mixing cases through one placeholder is
 * how «через 24 часов» ships.
 *
 * The number is never hardcoded: `thresholds` is on the wire precisely so that
 * moving the rule is one deploy rather than two.
 */
export const thresholdText = (ms: number): MoneyHealthText => {
  if (!Number.isFinite(ms) || ms < 0) return { key: `${KEY}.units.minutes`, values: { count: 0 } };
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return { key: `${KEY}.units.minutes`, values: { count: minutes } };
  const hours = Math.round(ms / 3_600_000);
  if (hours < 48) return { key: `${KEY}.units.hours`, values: { count: hours } };
  return { key: `${KEY}.units.days`, values: { count: Math.round(ms / 86_400_000) } };
};

/**
 * The hold's clock, in one line.
 *
 *   - no deadline        → «срок холда не задан» (NOT "истёк" — see rule 3)
 *   - deadline ahead     → «осталось 3ч 12м · 24.08.2026 14:20»
 *   - deadline behind    → «срок холда истёк · 22.08.2026 09:00»
 *
 * `now` is injectable so the boundary is a spec rather than a screen that
 * reads differently at 09:00 and at 17:00.
 */
export const holdDeadlineText = (
  holdExpiresAt: string | null,
  now: number = Date.now(),
): MoneyHealthText => {
  if (holdExpiresAt === null) return { key: `${KEY}.holds.deadlineUnset` };
  const at = Date.parse(holdExpiresAt);
  if (Number.isNaN(at)) return { key: `${KEY}.holds.deadlineUnset` };
  const formatted = formatDateTime(holdExpiresAt);
  if (at <= now) return { key: `${KEY}.holds.deadlinePassed`, values: { at: formatted } };
  return {
    key: `${KEY}.holds.timeLeft`,
    values: { duration: formatDurationShort(at - now), at: formatted },
  };
};
